import { useCallback, useRef, useState, type ReactNode } from 'react';
import { Icon } from '../primitives';
import type { IconName } from '../icons';
import { Timestamp } from '../status/StatusPieces';
import { actionLabel } from './actionLabels';
import type { SummaryTone } from './OperationSummary';

/**
 * The result of a check that runs on demand and answers once: a security
 * sweep, a server audit, a CDN check, a cache flush (UX_ARCHITECTURE.md,
 * rule K). It states one verdict, when it ran and what it covered; anything
 * more (counts, findings) is the children, and the raw output sits behind
 * "View details". It is composed by the screen that owns the check, so it
 * has no mode flags: a failure is the same shape with a different tone.
 */

type ResultTone = Exclude<SummaryTone, 'progress'>;

const toneIcon: Record<ResultTone, IconName> = {
  danger: 'alert-circle',
  warning: 'alert-triangle',
  success: 'check-circle',
  neutral: 'info',
};

export function OperationResult({
  tone,
  verdict,
  at,
  atLabel = 'Checked',
  scope,
  output,
  testId,
  children,
}: {
  tone: ResultTone;
  /** The one conclusion: "No critical problems or exposed files found". */
  verdict: ReactNode;
  /** When the answer was received (ISO). Never a time the server did not give. */
  at: string;
  /** The verb before the time: "Checked" for audits, "Ran" for an action. */
  atLabel?: string;
  /** What was covered, or what is known about the failure: one sentence. */
  scope?: ReactNode;
  /** Raw output, kept one click away (never removed). */
  output?: string | null;
  testId?: string;
  /** Counts, findings and recommendations the screen adds under the verdict. */
  children?: ReactNode;
}) {
  const raw = output?.trim() ? output : null;
  return (
    <div className={`op-result op-result--${tone}`} role={tone === 'danger' ? 'alert' : 'status'} data-testid={testId}>
      <div className="op-result__head">
        <span className="op-result__icon" aria-hidden="true">
          <Icon name={toneIcon[tone]} size={18} />
        </span>
        <div className="op-result__heading">
          <p className="op-result__verdict">{verdict}</p>
          <p className="op-result__when">
            {atLabel} <Timestamp iso={at} />
            {scope ? <> · {scope}</> : null}
          </p>
        </div>
      </div>
      {children}
      {raw ? (
        <details className="disclosure op-result__more">
          <summary>{actionLabel.viewDetails}</summary>
          <pre className="ssh-output op-result__output" tabIndex={0}>
            {raw}
          </pre>
        </details>
      ) : null}
    </div>
  );
}

/**
 * A check that did not answer: the same shape as a result, in the danger
 * tone, with the error the server gave in plain view (it is the cause, so it
 * is not folded) and any raw output behind "View details". The screen's own
 * "Retry" button is the next action; the failure does not invent another.
 */
export function OperationFailure({
  verdict,
  at,
  scope,
  reason,
  output,
  onRetry,
  testId,
}: {
  verdict: ReactNode;
  at: string;
  scope?: ReactNode;
  /** The error as reported. */
  reason?: string | null;
  output?: string | null;
  /** Runs the same operation again. Omit when the screen's own button is the Retry. */
  onRetry?: () => void;
  testId?: string;
}) {
  return (
    <OperationResult tone="danger" verdict={verdict} at={at} atLabel="Tried" scope={scope} output={output} testId={testId}>
      {reason ? (
        <p className="op-result__reason">
          <strong>Reported error:</strong> {reason}
        </p>
      ) : null}
      {onRetry ? (
        <div className="op-result__actions">
          <button type="button" className="button button--secondary button--small" onClick={onRetry}>
            {actionLabel.retry}
          </button>
        </div>
      ) : null}
    </OperationResult>
  );
}

/**
 * useOperationRun runs one on-demand check and keeps what is known about it:
 * the last answer with the time it arrived, and the last failure with its
 * time. A failure never erases the earlier answer, and an answer never hides
 * a later failure; each says when it happened.
 */
export function useOperationRun<T>(task: () => Promise<T>, fallbackMessage: string) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<{ value: T; at: string } | null>(null);
  const [failure, setFailure] = useState<{ message: string; at: string } | null>(null);
  const taskRef = useRef(task);
  taskRef.current = task;

  const run = useCallback(async () => {
    setRunning(true);
    setFailure(null);
    try {
      const value = await taskRef.current();
      setResult({ value, at: new Date().toISOString() });
    } catch (err) {
      setFailure({ message: err instanceof Error && err.message ? err.message : fallbackMessage, at: new Date().toISOString() });
    } finally {
      setRunning(false);
    }
  }, [fallbackMessage]);

  return { running, result, failure, run };
}
